import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Check, X, Home, ArrowLeft } from 'lucide-react';
import { QuestionType } from '../types';
import { useAuth } from '../context/AuthContext';

interface QuestionResult {
  question_text: string;
  question_type: QuestionType;
  options: string[];
  correct_answer?: number | number[];
  user_answer: number | number[];
  is_correct: boolean;
}

interface ExamResultsState {
  examName: string;
  score: number;
  passed: boolean;
  passScore: number;
  totalQuestions: number;
  correctAnswers: number;
  questions: QuestionResult[];
  examType?: string;
  sectionScores?: Array<{
    section_name: string;
    questions_in_section: number;
    correct_answers: number;
    section_score: number;
  }>;
}

const ExamResults: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const state = location.state as ExamResultsState;

  if (!state) {
    // Redirect to appropriate dashboard instead of root (which used to cause logout)
    const dashboardPath = user?.is_admin ? '/admin-dashboard' : '/user-dashboard';
    navigate(dashboardPath, { replace: true });
    return null;
  }

  const isPractice = state.examType === 'practice';

  const renderAnswerStatus = (question: QuestionResult, optionIndex: number) => {
    const isMultiSelect = question.question_type === 'multi_select';
    const userAnswers = Array.isArray(question.user_answer) ? question.user_answer : [question.user_answer];
    const isUserSelected = userAnswers.includes(optionIndex);

    if (!isPractice || question.correct_answer === undefined) {
      if (isUserSelected) {
        return question.is_correct ? (
          <Check className="text-green-500" />
        ) : (
          <X className="text-red-500" />
        );
      }
      return null;
    }

    const correctAnswers = Array.isArray(question.correct_answer) ? question.correct_answer : [question.correct_answer];
    const isCorrectAnswer = correctAnswers.includes(optionIndex);

    if (isMultiSelect) {
      if (isUserSelected && isCorrectAnswer) {
        return <Check className="text-green-500" />;
      } else if (isUserSelected && !isCorrectAnswer) {
        return <X className="text-red-500" />;
      } else if (!isUserSelected && isCorrectAnswer) {
        return <Check className="text-yellow-500" />;
      }
    } else {
      if (optionIndex === question.user_answer) {
        return question.is_correct ? (
          <Check className="text-green-500" />
        ) : (
          <X className="text-red-500" />
        );
      } else if (optionIndex === question.correct_answer) {
        return <Check className="text-yellow-500" />;
      }
    }
    return null;
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="bg-gray-800 rounded-lg p-6 mb-8">
        <h1 className="text-2xl font-bold mb-4">{state.examName} - Results</h1>
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div>
            <p className="text-gray-400">Score</p>
            <p className="text-2xl font-bold">{state.score}%</p>
          </div>
          <div>
            <p className="text-gray-400">Status</p>
            <p className={`text-2xl font-bold ${state.passed ? 'text-green-500' : 'text-red-500'}`}>
              {state.passed ? 'PASSED' : 'FAILED'}
            </p>
          </div>
          <div>
            <p className="text-gray-400">Correct Answers</p>
            <p className="text-2xl font-bold">{state.correctAnswers} / {state.totalQuestions}</p>
          </div>
          <div>
            <p className="text-gray-400">Pass Score</p>
            <p className="text-2xl font-bold">{state.passScore}%</p>
          </div>
        </div>
      </div>

      {/* Section Scores Breakdown */}
      {state.sectionScores && state.sectionScores.length > 0 && (
        <div className="bg-gray-800 rounded-lg p-6 mb-8">
          <h2 className="text-xl font-bold mb-4">Performance by Subject Category</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {state.sectionScores.map((section, index) => (
              <div key={index} className="bg-gray-700 rounded-lg p-4">
                <h3 className="text-lg font-semibold mb-2">{section.section_name}</h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Score:</span>
                    <span className={`font-bold ${
                      section.section_score >= 80 ? 'text-green-500' :
                      section.section_score >= 70 ? 'text-yellow-500' :
                      'text-red-500'
                    }`}>
                      {section.section_score.toFixed(1)}%
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Correct:</span>
                    <span className="font-medium">
                      {section.correct_answers} / {section.questions_in_section}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="space-y-6">
        {state.questions.map((question, index) => (
          <div key={index} className="bg-gray-800 rounded-lg p-6">
            <h3 className="text-xl font-semibold mb-2">
              Question {index + 1} - {question.is_correct ? (
                <span className="text-green-500">Correct</span>
              ) : (
                <span className="text-red-500">Incorrect</span>
              )}
            </h3>
            <p className="mb-4">{question.question_text}</p>
            <div className="space-y-2">
              {question.options.map((option, optionIndex) => (
                <div
                  key={optionIndex}
                  className={`flex items-center justify-between p-3 rounded-lg ${
                    question.question_type === 'multi_select'
                      ? Array.isArray(question.user_answer) && question.user_answer.includes(optionIndex)
                        ? 'bg-blue-900'
                        : 'bg-gray-700'
                      : question.user_answer === optionIndex
                        ? 'bg-blue-900'
                        : 'bg-gray-700'
                  }`}
                >
                  <span>{option}</span>
                  <div className="flex items-center space-x-2">
                    {renderAnswerStatus(question, optionIndex)}
                  </div>
                </div>
              ))}
            </div>
            {question.question_type === 'multi_select' && (
              <p className="mt-2 text-sm text-gray-400">
                This was a multi-select question. All correct options should be selected.
              </p>
            )}
          </div>
        ))}
      </div>

      <div className="mt-8 flex justify-center">
        <button
          onClick={() => {
            const dashboardPath = user?.is_admin ? '/admin-dashboard' : '/user-dashboard';
            navigate(dashboardPath);
          }}
          className="flex items-center bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-500 transition duration-300"
        >
          <Home className="mr-2" /> Return to Dashboard
        </button>
      </div>
    </div>
  );
};

export default ExamResults;